import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ServerTableActions } from 'shared/ui/ServerTable/types';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ServerTable } from 'shared/ui/ServerTable';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { ContractDetailsDrawer, contractDetailsDrawerActions } from 'widgets/ContractDetailsDrawer';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { ExportEntity, ExportModal, getDepositColumnKeys, useOpenExportModal } from 'features/ExportModal';
import { useTableFilterContext } from 'features/TableFilter';
import { ReactComponent as ExportIcon } from 'shared/assets/icons/ExportIcon.svg';
import { useGetCurrencySymbol } from 'app/appState';
import { DepositListFilter } from './DepositListFilter';
import { transformDepositsListFilters } from '../utils/transformDepositsListFilters';
import classNames from 'classnames';
import { Deposit, getDepositStatus, useGetPaginatedDepositsQuery } from 'entities/Deposit';
import { getDepositStatusColor } from './constants/constants';
import { getUserName } from 'entities/User';
import dayjs from 'dayjs';
import { getLastItem } from 'shared/utils/helpers/array';
import { DepositHistoryModal } from 'features/DepositHistoryModal';
import { Tag } from 'antd';
import { getColorTagByAccountType } from 'shared/utils/helpers/getColorTagByAccountType';

export const DepositsTable: FC = memo(() => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const currencySymbol = useGetCurrencySymbol();

  const [isDepositHistoryModalOpened, setIsDepositHistoryModalOpened] = useState(false);
  const [selectedDepositId, setSelectedDepositId] = useState<string>('');

  const openDepositHistoryModal = useCallback((data: AnyObject) => {
    const record = data as Deposit;
    setSelectedDepositId(record.depositId);
    setIsDepositHistoryModalOpened(true);
  }, []);

  const closeDepositHistoryModal = useCallback(() => {
    setIsDepositHistoryModalOpened(false);
    setSelectedDepositId('');
  }, []);

  const { openExportModal, isExportEnabled } = useOpenExportModal();

  const { t } = useAppTranslation(['contracts', 'common']);

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const statusRefDate = useMemo(() => {
    const today = dayjs().startOf('day');

    return appliedFilters.statusRefDate?.value ? dayjs(appliedFilters.statusRefDate.value).startOf('day') : today;
  }, [appliedFilters.statusRefDate]);

  const goToUserContracts = useCallback(
    (userId: string): void => {
      navigate(`${AppRoutes.USERS}/${userId}/contracts`);
    },
    [navigate],
  );

  const openContractDetails = useCallback(
    (contractId: string): void => {
      dispatch(contractDetailsDrawerActions.setOpenContractDetails(contractId));
    },
    [dispatch],
  );

  const columns = useMemo<ColumnsType<Deposit>>(
    () => [
      {
        title: t('Customer'),
        width: '20%',
        key: 'user',
        render: (_, record) => (
          <div>
            <div
              className="text-accent font-semibold underline cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                goToUserContracts(record.user.userId);
              }}
            >
              {getUserName(record.user)}
            </div>
            <Tag className="w-fit" color={getColorTagByAccountType(record.user.accountType)}>
              {t(record.user.accountType, { ns: 'user' })}
            </Tag>
          </div>
        ),
      },
      {
        title: t('Invoice number'),
        key: 'documentNumber',
        width: '15%',
        render: (_, record) => <div className="text-primary font-normal">{record.invoiceItem.invoice.documentNumber}</div>,
      },
      {
        title: t('Contract'),
        width: '15%',
        key: 'contractWarehouse',
        render: (_, record) => {
          return (
            <div>
              <div
                className="text-accent underline cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  openContractDetails(record.contract.contractId);
                }}
              >
                #{record.contract.contractNumber}
              </div>
              <div className="mt-1 text-primary font-normal text-m text-gray-600">{record.contract.warehouse.name}</div>
            </div>
          );
        },
      },
      {
        title: t('Status ({{statusRefDate}})', { statusRefDate: getClientDateFormat(statusRefDate) }),
        width: '15%',
        key: 'status',
        render: (_, record) => {
          const status = getDepositStatus(record);
          const statusColor = getDepositStatusColor(status);

          return <div className={`font-semibold ${statusColor}`}>{t(status)}</div>;
        },
      },
      {
        title: t('Initial Amount'),
        key: 'initialDepositAmount',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: '10%',
        render: (_, record) => {
          return (
            <div className="text-primary font-normal">
              {record.initialDepositAmount} {currencySymbol}
            </div>
          );
        },
      },
      {
        title: t('Amount ({{statusRefDate}})', { statusRefDate: getClientDateFormat(statusRefDate) }),
        key: 'depositAmount',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: '10%',
        render: (_, record) => {
          const latestHistory = getLastItem(record.depositHistory);
          const depositAmount = latestHistory ? latestHistory.depositAmount : 0;
          return (
            <div className="text-primary font-normal">
              {depositAmount} {currencySymbol}
            </div>
          );
        },
      },
      {
        title: t('Charged ({{statusRefDate}})', { statusRefDate: getClientDateFormat(statusRefDate) }),
        key: 'chargedDepositAmount',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: '10%',
        render: (_, record) => {
          const latestHistory = getLastItem(record.depositHistory);
          const chargedDepositAmount = latestHistory ? latestHistory.chargedDepositAmount : 0;
          return (
            <div className="text-primary font-normal">
              {chargedDepositAmount} {currencySymbol}
            </div>
          );
        },
      },
      {
        title: t('Cancelled ({{statusRefDate}})', { statusRefDate: getClientDateFormat(statusRefDate) }),
        key: 'cancelledDepositAmount',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: '10%',
        render: (_, record) => {
          const latestHistory = getLastItem(record.depositHistory);
          const cancelledDepositAmount = latestHistory ? latestHistory.cancelledDepositAmount : 0;
          return (
            <div className="text-primary font-normal">
              {cancelledDepositAmount} {currencySymbol}
            </div>
          );
        },
      },
      {
        title: t('Refunded ({{statusRefDate}})', { statusRefDate: getClientDateFormat(statusRefDate) }),
        key: 'refundedDepositAmount',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: '10%',
        render: (_, record) => {
          const latestHistory = getLastItem(record.depositHistory);
          const refundedDepositAmount = latestHistory ? latestHistory.refundedDepositAmount : 0;
          return (
            <div className="text-primary font-normal">
              {refundedDepositAmount} {currencySymbol}
            </div>
          );
        },
      },
      {
        title: t('Created date'),
        key: 'createdAt',
        width: '10%',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => <div className="text-primary font-normal">{getClientDateFormat(record.createdAt)}</div>,
      },
    ],
    [t, statusRefDate, goToUserContracts, openContractDetails, currencySymbol],
  );

  const tableActions: Array<ServerTableActions<Deposit>> = useMemo(() => {
    const exportAction: Array<ServerTableActions<Deposit>> = isExportEnabled
      ? [
          {
            name: t('Export', { ns: 'common' }),
            theme: 'secondary',
            icon: <ExportIcon />,
            iconPosition: 'prev',
            onClick: () => {
              openExportModal({
                filters: transformDepositsListFilters(appliedFilters),
                columns: getDepositColumnKeys(t),
                entity: ExportEntity.DEPOSIT,
              });
            },
          },
        ]
      : [];

    return [
      ...exportAction,
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ];
  }, [appliedFilters, isExportEnabled, openExportModal, setFiltersOpened, t]);

  const highlightRow = (record: Deposit): string => {
    return classNames({
      'bg-secondary': !record.contract.isApplicable,
      'cursor-pointer': true,
    });
  };

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7">{t('Deposits')}</div>
      <ServerTable
        columns={columns}
        fetchData={useGetPaginatedDepositsQuery}
        rowKey="depositId"
        search
        tableActionsPosition="prev"
        searchPlaceholder={t('Enter customer name or invoice | contract number')}
        tableActions={tableActions}
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        defaultFilters={transformDepositsListFilters(appliedFilters)}
        rowClassName={highlightRow}
        onRowClick={openDepositHistoryModal}
      />
      <ExportModal />
      <ContractDetailsDrawer />
      <DepositListFilter />
      <DepositHistoryModal isOpened={isDepositHistoryModalOpened} depositId={selectedDepositId} onClose={closeDepositHistoryModal} />
    </div>
  );
});
